// src/hooks/useBridgeVerify.ts
import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { ensureBridgeToken } from '@/lib/bridge/bridgeAuth';

/**
 * useBridgeVerify — обёртка над BFF маршрутом верификации.
 *
 * Требуемый BFF:
 *   POST /api/gateway-v1/bridge/verify
 *   Headers: Authorization: Bearer <bridgeToken>, Idempotency-Key: <uuid>
 *   Body: { externalAccountId: string, kind: "address"|"identity", meta?: object }
 *   200:
 *     { ok:true, verificationId:string, state:"created"|"pending"|"passed"|"failed", reason?:string }
 *   4xx/5xx:
 *     { ok:false, code:string, message?:string }
 */

const VerifyKind = z.enum(['address', 'identity']);

export const VerifyRequest = z.object({
  externalAccountId: z.string().min(3),
  kind: VerifyKind,
  meta: z.record(z.any()).optional(),
});
export type VerifyRequestInput = z.infer<typeof VerifyRequest>;

const OkResp = z.object({
  ok: z.literal(true),
  verificationId: z.string().min(6),
  state: z.enum(['created', 'pending', 'passed', 'failed']),
  reason: z.string().optional(),
});
const ErrResp = z.object({
  ok: z.literal(false),
  code: z.string().min(1),
  message: z.string().optional(),
});
const UnionResp = z.union([OkResp, ErrResp]);

type State =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; verificationId: string; state: 'created' | 'pending' | 'passed' | 'failed'; reason?: string }
  | { status: 'error'; error: string };

function idemKey(): string {
  const rnd = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `idem-${rnd}`;
}

async function withTimeout<T>(p: Promise<T>, ms: number, signal?: AbortSignal): Promise<T> {
  if (typeof AbortController !== 'undefined') {
    const c = new AbortController();
    const t = setTimeout(() => c.abort(), ms);
    const composite = new AbortController();
    const onAbort = () => composite.abort();
    c.signal.addEventListener('abort', onAbort);
    signal?.addEventListener('abort', onAbort);
    try {
      // @ts-expect-error composite.signal доступен в средах с AbortController
      return await Promise.race([p, new Promise<never>((_, rej) => composite.signal.addEventListener('abort', () => rej(new Error('timeout'))))]);
    } finally {
      clearTimeout(t);
      c.signal.removeEventListener('abort', onAbort);
      signal?.removeEventListener('abort', onAbort);
    }
  }
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }).catch((e) => { clearTimeout(t); reject(e); });
  });
}

export function useBridgeVerify() {
  const [state, setState] = useState<State>({ status: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => setState({ status: 'idle' }), []);

  const submit = useCallback(async (input: VerifyRequestInput) => {
    // отменяем предыдущий ран
    abortRef.current?.abort();
    abortRef.current = typeof AbortController !== 'undefined' ? new AbortController() : null;

    setState({ status: 'submitting' });

    try {
      const body = VerifyRequest.parse(input);
      const token = await ensureBridgeToken();
      const r = await withTimeout(
        fetch('/api/gateway-v1/bridge/verify', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Idempotency-Key': idemKey(),
          },
          body: JSON.stringify(body),
          signal: abortRef.current?.signal,
        }),
        12000,
        abortRef.current?.signal
      );

      const text = await r.text();
      let payload: unknown = null;
      try { payload = text ? JSON.parse(text) : null; } catch { /* ignore */ }

      if (!r.ok) {
        const err = ErrResp.safeParse(payload);
        const msg = err.success ? (err.data.message ?? err.data.code) : `HTTP_${r.status}`;
        setState({ status: 'error', error: msg });
        return;
      }

      const parsed = UnionResp.safeParse(payload);
      if (!parsed.success) {
        setState({ status: 'error', error: 'Некорректный ответ сервера.' });
        return;
      }
      if (parsed.data.ok) {
        setState({
          status: 'success',
          verificationId: parsed.data.verificationId,
          state: parsed.data.state,
          reason: parsed.data.reason,
        });
      } else {
        setState({ status: 'error', error: parsed.data.message ?? parsed.data.code });
      }
    } catch (e) {
      const message = e instanceof Error ? (e.name === 'AbortError' || e.message === 'timeout' ? 'Превышено время ожидания.' : e.message) : 'Сетевая ошибка.';
      setState({ status: 'error', error: message });
    }
  }, []);

  return useMemo(() => ({
    state,
    submit,
    reset,
    isIdle: state.status === 'idle',
    isLoading: state.status === 'submitting',
    isSuccess: state.status === 'success',
    isError: state.status === 'error',
  }), [state, submit, reset]);
}